// SUN-174 mockup 2: "<VENDOR> · products" - what this vendor supplies, with rate, lead time, preferred.
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Loader2, Pencil, Trash2, X } from 'lucide-react';
import { api } from '@/utils/api';
import { useAuthStore } from '@/stores/auth';
import { LinkWindow } from './LinkWindow';
import { apiError, canEditLinks, fmtLead, fmtRate, type VendorLink } from './vendorLinks';
import { Modal, useModalClose } from '@/components/ui/Modal';
import type { ButtonHTMLAttributes } from 'react';

interface Props {
  vendor: { id: string; name: string };
  onClose: () => void;
  /** The vendors list reloads its product counts. */
  onChanged: () => void;
  /** Room for the Excel task's "⬇ Download format" / "⬆ Upload Excel" buttons. */
  headerActions?: ReactNode;
  /** SUN-180: change it to reload the list (after an Excel save) without closing the drawer. */
  reloadToken?: number;
}

export function VendorProductsDrawer({ vendor, onClose, onChanged, headerActions, reloadToken }: Props) {
  const { user } = useAuthStore();
  const editor = canEditLinks(user as any);
  const [links, setLinks] = useState<VendorLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<VendorLink | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setError(null);
      const res = await api.get(`/vendors/${vendor.id}/products`);
      setLinks(res.data?.data?.links ?? res.data?.data ?? []);
    } catch (e: any) {
      setError(apiError(e, 'Could not load this vendor’s products.'));
    } finally { setLoading(false); }
  }, [vendor.id, reloadToken]);
  useEffect(() => { load(); }, [load]);

  const changed = async () => { await load(); onChanged(); };
  const act = async (id: string, fn: () => Promise<unknown>, fail: string) => {
    setBusy(id); setError(null);
    try { await fn(); await changed(); } catch (e: any) { setError(apiError(e, fail)); } finally { setBusy(null); }
  };

  const showRate = editor || links.some((l) => l.rate !== null && l.rate !== undefined);
  return (
    <>
      <Modal open onClose={onClose} side="right" ariaLabel={`${vendor.name} · products`} className="!max-w-3xl">
      <div>
        <div className="sticky top-0 z-10 flex flex-wrap items-center gap-2 border-b border-gray-200 bg-white px-5 py-3">
          <h2 className="mr-auto text-lg font-semibold text-gray-900">{vendor.name} · products</h2>
          {editor && <button type="button" onClick={() => setAdding(true)} className="rounded-lg bg-gray-900 px-3 py-1.5 text-sm font-medium text-white">＋ Add product</button>}
          <div data-testid="vendor-products-header-actions" className="flex gap-2">{headerActions}</div>
          <RequestCloseButton aria-label="Close" className="text-gray-400 hover:text-gray-600"><X size={20} /></RequestCloseButton>
        </div>
        {error && <p role="alert" className="m-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin" /></div>
        ) : links.length === 0 ? (
          <p className="py-16 text-center text-sm text-gray-500">No products linked to this vendor yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-gray-200 bg-gray-50 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-4 py-2">Product</th><th className="px-4 py-2">Vendor&apos;s code</th>
                {showRate && <th className="px-4 py-2 text-right">Rate ₹</th>}
                <th className="px-4 py-2">Unit</th><th className="px-4 py-2">Lead time</th><th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {links.map((l) => (
                <tr key={l.id} className="align-top">
                  <td className="px-4 py-2"><p className="font-medium text-gray-900">{l.product.name}</p>{l.product.sku && <p className="font-mono text-xs text-gray-500">{l.product.sku}</p>}</td>
                  <td className="px-4 py-2 text-gray-600">{l.vendorItemCode || '—'}</td>
                  {showRate && <td className="px-4 py-2 text-right">{fmtRate(l.rate)}</td>}
                  <td className="px-4 py-2 text-gray-600">{l.unit || '—'}</td>
                  <td className="px-4 py-2 text-gray-600">{fmtLead(l.leadTimeDays)}</td>
                  <td className="whitespace-nowrap px-4 py-2 text-right">
                    {!l.isPreferred && <span className="mr-2 text-xs text-gray-400">2nd choice</span>}
                    {l.isPreferred
                      ? <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800">★ Preferred</span>
                      : editor && <button type="button" disabled={busy === l.id} onClick={() => act(l.id, () => api.post(`/vendors/products/${l.id}/preferred`), 'Could not make this vendor preferred.')} className="rounded border border-gray-300 px-2 py-0.5 text-xs text-gray-700 hover:bg-gray-50">Make preferred</button>}
                    {editor && (
                      <>
                        <button type="button" title="Edit link" aria-label={`Edit ${l.product.name}`} onClick={() => setEditing(l)} className="ml-2 rounded p-1 text-gray-400 hover:text-gray-700"><Pencil size={14} /></button>
                        <button type="button" title="Remove link" aria-label={`Remove ${l.product.name}`} disabled={busy === l.id}
                          onClick={() => { if (confirm(`Remove ${l.product.name} from ${vendor.name}? Only the link is removed.`)) act(l.id, () => api.delete(`/vendors/products/${l.id}`), 'Could not remove the link.'); }}
                          className="rounded p-1 text-gray-400 hover:text-red-600"><Trash2 size={14} /></button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      </Modal>
      {adding && <LinkWindow pick="product" fixed={vendor} onClose={() => setAdding(false)} onSaved={async () => { setAdding(false); await changed(); }} />}
      {editing && (
        <LinkWindow pick="product" fixed={{ id: editing.productId, name: editing.product.name }} edit={editing}
          onClose={() => setEditing(null)} onSaved={async () => { setEditing(null); await changed(); }} />
      )}
    </>
  );
}

function RequestCloseButton({ children, ...props }: ButtonHTMLAttributes<HTMLButtonElement>) {
  const { requestClose } = useModalClose();
  return <button type="button" {...props} onClick={requestClose}>{children}</button>;
}
